import { inject, Injectable, Type } from "@angular/core";

import { ChoosePlanModalComponent } from "@billing/components/choose-plan-modal/choose-plan-modal.component";
import { DocumentTreePickerModalComponent } from "@documentation/components/modals/document-tree-picker-modal.component";
import { PromptModalComponent } from "@documentation/components/modals/prompt-modal.component";
import { ShareDocumentModalComponent } from "@documentation/components/modals/share-document-modal.component";
import { DocumentListItemModel } from "@documentation/models/document.model";
import { FolderModel } from "@documentation/models/folder.model";
import { ThankYouForPurchaseModalComponent } from "@features/billing/components/thank-you-modal/thank-you-for-purchase-modal.component";
import { CreateDocumentOrFolderModalComponent } from "@features/documentation/components/modals/create-document-or-folder-modal.component";
import { MoveFolderOrDocumentModalComponent } from "@features/documentation/components/modals/move-folder-or-document-modal.component";
import { ShareAiRoleModalComponent } from "@features/documentation/components/modals/share-ai-role-modal.component";
import { AuthModalComponent } from "@login/components/modal/auth-modal.component";
import { PasswordResetModalComponent } from "@login/components/modal/password-reset-modal.component";
import { RegisterModalComponent } from "@login/components/modal/register-modal.component";
import { WelcomeModalComponent } from "@login/components/modal/welcome-modal.component";
import { NgbModal, NgbModalOptions, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { BaseModalComponent } from "@shared/components/modal/base-modal.component";
import { ImagePreviewModalComponent } from "@shared/components/modal/image-preview-modal/image-preview-modal.component";
import { UserProfileModalComponent } from "@shared/components/modal/user-profile-modal/user-profile-modal.component";
import { User } from "@shared/models/user.model";

import { ConfirmationModalComponent } from "./confirmation-modal/confirmation-modal.component";

export interface PromptModalOptions {
  title?: string;
  label?: string;
  currentValue?: string;
  confirmButtonText?: string;
  required?: boolean;
}

type ModalOptions = NgbModalOptions & { closeOtherModals?: boolean; autoFocus?: boolean };

@Injectable({
  providedIn: "root"
})
export class ModalService {
  private readonly _modalService = inject(NgbModal);

  // Track active modals to auto-close previous ones
  private readonly _activeModals = new Set<NgbModalRef>();

  async unsavedChanges(): Promise<boolean> {
    const choice = await this.confirmChoices(
      "Unsaved Changes",
      "You have unsaved changes that will be lost. How would you like to proceed?",
      [
        { id: "save", label: "Save & Continue", btnClass: "btn-success" },
        { id: "cancel", label: "Cancel", btnClass: "btn-secondary" },
        { id: "ok", label: "OK", btnClass: "btn-primary" }
      ]
    );
    return choice === "ok" || choice === "save";
  }

  confirm(title: string, message: string, confirmText: string = "OK", options: ModalOptions = {}): Promise<boolean> {
    options.autoFocus = options?.autoFocus ?? false;
    const modalRef = this.openModal(ConfirmationModalComponent, options);

    modalRef.componentInstance.title.set(title);
    modalRef.componentInstance.message.set(message);
    modalRef.componentInstance.confirmText.set(confirmText);

    return modalRef.result.then((result) => result === true);
  }

  // Show a confirmation with multiple choices. Returns the id of the selected action.
  confirmChoices(
    title: string,
    message: string,
    choices: {
      id: string;
      label: string;
      btnClass: string;
      buttonSize?: "sm" | "md" | "lg";
    }[],
    options?: ModalOptions
  ): Promise<string | null> {
    choices.forEach((choice) => {
      choice.buttonSize = choice.buttonSize ?? "md";
    });

    const modalRef = this.openModal(ConfirmationModalComponent, options);

    modalRef.componentInstance.title.set(title);
    modalRef.componentInstance.message.set(message);
    modalRef.componentInstance.choices.set(choices);

    return modalRef.result.then(
      (result) => (typeof result === "string" ? result : null),
      () => null
    );
  }

  openShareDocument(documentId: string): void {
    const modalRef = this.openModal(ShareDocumentModalComponent, {
      modalDialogClass: "modal-header-border",
      size: "lg",
      keyboard: false,
      autoFocus: false
    });
    modalRef.componentInstance.documentId.set(documentId);
  }

  openShareAiRole(roleId: string): void {
    const modalRef = this.openModal(ShareAiRoleModalComponent, {
      modalDialogClass: "modal-header-border",
      size: "lg",
      keyboard: false,
      autoFocus: false
    });
    modalRef.componentInstance.roleId.set(roleId);
  }

  openImagePreview(dataUrl: string, title = "Image preview"): void {
    try {
      const modalRef = this.openModal(ImagePreviewModalComponent, { size: "lg" });
      modalRef.componentInstance.src.set(dataUrl);
      modalRef.componentInstance.title.set(title);
    } catch {
      // Fallback: open in new tab if modal fails - this error will be caught by global handler
      const win = window.open();
      if (win) {
        win.document.write(`<img src="${dataUrl}" style="max-width:100%;height:auto" />`);
      }
    }
  }

  openSignupModal(isUpgradeFlow: boolean = false, isExpired: boolean = false): NgbModalRef {
    const modalRef = this.openModal(AuthModalComponent, {
      modalDialogClass: "modal-body-auth",
      centered: true,
      backdrop: "static",
      keyboard: !isExpired,
      size: "sm"
    });

    // Pass inputs to component
    modalRef.componentInstance.mode.set("signup");
    modalRef.componentInstance.isUpgradeFlow.set(isUpgradeFlow);
    modalRef.componentInstance.isExpired.set(isExpired);

    return modalRef;
  }

  openLoginModal(isUpgradeFlow = false, isExpired: boolean = false): NgbModalRef {
    const modalRef = this.openModal(AuthModalComponent, {
      modalDialogClass: "modal-body-auth",
      size: "sm",
      keyboard: false
    });

    // Pass inputs to component
    modalRef.componentInstance.mode.set("login");
    modalRef.componentInstance.isUpgradeFlow.set(isUpgradeFlow);
    modalRef.componentInstance.isExpired.set(isExpired);

    return modalRef;
  }

  openRegisterModal(email: string, organizationId: string | undefined): NgbModalRef {
    const modalRef = this.openModal(RegisterModalComponent, {
      modalDialogClass: "modal-body-auth",
      size: "md",
      keyboard: false
    });

    modalRef.componentInstance.email.set(email);
    modalRef.componentInstance.organizationId.set(organizationId);

    return modalRef;
  }

  openUpgradeModel(user: User, isExpired: boolean = false): NgbModalRef | null {
    if (user.isTemporary) {
      // Use auth modal in signup mode for upgrade flow
      return this.openSignupModal(true, isExpired);
    } else {
      this.openBillingModal(!!user.organization?.planId);
      return null;
    }
  }

  openBillingModal(
    hasExistingPlan: boolean,
    options?: { headerTitle?: string; headerSubtitle?: string; showSkipButton?: boolean; hasExistingPlan: boolean }
  ): NgbModalRef {
    const modalRef = this.openModal(ChoosePlanModalComponent, {
      size: "xl"
    });

    const defaultTitle = hasExistingPlan ? "Change current plan" : "You're all signed up! Choose your plan";

    const defaultSubtitle = hasExistingPlan
      ? "Select a new plan that better fits your needs."
      : "Start by selecting the plan that's right for you.";

    if (options?.headerTitle) {
      modalRef.componentInstance.headerTitle.set(options.headerTitle);
    } else {
      modalRef.componentInstance.headerTitle.set(defaultTitle);
    }

    if (options?.headerSubtitle) {
      modalRef.componentInstance.headerSubtitle.set(options.headerSubtitle);
    } else {
      modalRef.componentInstance.headerSubtitle.set(defaultSubtitle);
    }

    if (options?.showSkipButton !== undefined) {
      modalRef.componentInstance.showSkipButton.set(options.showSkipButton);
    }

    return modalRef;
  }

  async confirmTempLogout(): Promise<"upgrade" | "logout" | null> {
    const choice = await this.confirmChoices(
      "Don't lose your work",
      "You're using a temporary trial. If you sign out now, this account and all documents will be permanently deleted.",
      [
        { id: "cancel", label: "Cancel", btnClass: "btn-outline-secondary" },
        { id: "logout", label: "Delete and sign out", btnClass: "btn-outline-danger" },
        { id: "upgrade", label: "Create free account", btnClass: "btn-primary" }
      ]
    );
    return choice === "upgrade" ? "upgrade" : choice === "logout" ? "logout" : null;
  }

  openWelcomeModal(isInviteRegistration: boolean = false): void {
    const modalRef = this.openModal(WelcomeModalComponent, {
      modalDialogClass: "modal-body-welcome",
      size: "sm",
      keyboard: false
    });
    modalRef.componentInstance.isInviteRegistration.set(isInviteRegistration);
  }

  openUserProfileModal(user: User): void {
    const modalRef = this.openModal(UserProfileModalComponent, {
      size: "md"
    });
    modalRef.componentInstance.user.set(user);
  }

  openPasswordResetModal(email?: string, fromProfile: boolean = false): NgbModalRef {
    const modalRef = this.openModal(PasswordResetModalComponent, {
      modalDialogClass: "modal-body-auth",
      size: "sm",
      keyboard: false
    });

    if (email && fromProfile) {
      modalRef.componentInstance.initializeFromProfile(email);
    }

    return modalRef;
  }

  openPasswordResetModalWithToken(token: string, email: string): void {
    const modalRef = this.openModal(PasswordResetModalComponent, {
      modalDialogClass: "modal-body-auth",
      size: "sm",
      keyboard: false
    });

    modalRef.componentInstance.initializeWithToken(token, email);
  }

  openThankYouForPurchaseModal(): void {
    this.openModal(ThankYouForPurchaseModalComponent, {
      keyboard: false
    });
  }

  openRenameEntityModal(currentName: string, title?: string, label?: string): NgbModalRef {
    return this.openPromptModal(
      {
        currentValue: currentName,
        confirmButtonText: "Rename",
        title,
        label
      },
      {
        keyboard: true
      }
    );
  }

  openCreateDocumentModal(targetFolder?: FolderModel): NgbModalRef {
    const modalRef = this.openModal(CreateDocumentOrFolderModalComponent, {
      size: "md",
      modalDialogClass: "modal-header-border",
      keyboard: true
    });

    modalRef.componentInstance.targetFolder.set(targetFolder);

    return modalRef;
  }

  openMoveEntityModal(doc: DocumentListItemModel): NgbModalRef {
    const modalRef = this.openModal(MoveFolderOrDocumentModalComponent, {
      modalDialogClass: "modal-header-border"
    });

    modalRef.componentInstance.document.set(doc);

    return modalRef;
  }

  openDocumentTreePickerModal(selectedDocs?: DocumentListItemModel[], hiddenDocumentsIds?: string[]): NgbModalRef {
    const modalRef = this.openModal(DocumentTreePickerModalComponent, {
      modalDialogClass: "modal-header-border",
      scrollable: false
    });

    if (selectedDocs) {
      modalRef.componentInstance.selectedDocuments.set(selectedDocs);
    }

    if (hiddenDocumentsIds) {
      modalRef.componentInstance.hiddenDocumentsIds.set(hiddenDocumentsIds);
    }

    return modalRef;
  }

  confirmMoveDocIntoPublicFolder(): Promise<boolean> {
    return this.confirm("Move document?", "This document will become public. Are you sure you want to continue?", "Ok", {
      closeOtherModals: false
    });
  }

  openPromptModal(options?: PromptModalOptions, modalOptions?: ModalOptions): NgbModalRef {
    const modalRef = this.openModal(PromptModalComponent, {
      size: "md",
      modalDialogClass: "modal-header-border",
      ...modalOptions
    });

    const instance = modalRef.componentInstance as PromptModalComponent;

    if (options?.title !== undefined) instance.title.set(options.title);
    if (options?.label !== undefined) instance.label.set(options.label);
    if (options?.currentValue !== undefined) instance.currentValue.set(options.currentValue);
    if (options?.confirmButtonText !== undefined) instance.confirmButtonText.set(options.confirmButtonText);
    if (options?.required !== undefined) instance.required.set(options.required);

    return modalRef;
  }

  openModal<T extends BaseModalComponent>(component: Type<T>, options: ModalOptions = {}): NgbModalRef {
    options.closeOtherModals = options.closeOtherModals ?? true;

    // Close all existing active modals before opening a new one
    if (options.closeOtherModals) {
      this.closeAllActiveModals();
    }

    // Set default options
    options.scrollable = options.scrollable ?? true;
    options.centered = options.centered ?? true;
    options.backdrop = options.backdrop ?? "static";
    options.keyboard = options.keyboard ?? true;

    // Open the new modal
    const modalRef = this._modalService.open(component, options);
    modalRef.componentInstance.autoFocus.set(options.autoFocus ?? true);

    // Track the active modal
    this._activeModals.add(modalRef);

    // Handle modal dismissal
    modalRef.result.finally(() => {
      this._activeModals.delete(modalRef);
    });

    return modalRef;
  }

  /**
   * Close all currently active modals
   */
  private closeAllActiveModals(): void {
    // Create a copy of the set to avoid modification during iteration
    const modalsToClose = Array.from(this._activeModals);

    modalsToClose.forEach((modalRef) => {
      try {
        modalRef.close();
      } catch {
        // Modal might already be closed, ignore the error
        this._activeModals.delete(modalRef);
      }
    });

    // Clear the set
    this._activeModals.clear();
  }

  /**
   * Manually close all active modals (useful for cleanup)
   */
  closeAllModals(): void {
    this.closeAllActiveModals();
  }

  /**
   * Get the count of currently active modals
   */
  getActiveModalCount(): number {
    return this._activeModals.size;
  }

  /**
   * Check if there are any active modals
   */
  hasActiveModals(): boolean {
    return this._activeModals.size > 0;
  }
}
